/**
 * HypeFly Analytics-Tracker (cookiefrei, DSGVO-freundlich)
 * --------------------------------------------------------
 * - Session-ID via `crypto.randomUUID()` in `sessionStorage` (verfällt beim
 *   Browser-Close, kein Cookie, keine Cross-Session-Verknüpfung).
 * - Backend hasht die ID zusätzlich mit Salt — der DB-Admin kann sie
 *   nicht mit einem einzelnen User verknüpfen.
 * - `vt_code` (Vertreter-Tracking): einmalig aus URL `?vt=` gelesen und in
 *   `sessionStorage` gespeichert, dann aus URL entfernt.
 * - Heartbeat alle 30s, solange Tab sichtbar. Beim `pagehide` /
 *   `beforeunload` finaler Heartbeat via `navigator.sendBeacon`.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
const SESSION_KEY = "hf_session_id";
const VT_KEY = "hf_vt_code";
const ATTRIBUTION_KEY = "hf_attribution";
const HEARTBEAT_INTERVAL_MS = 30_000;

let lastEventAt = 0;
let heartbeatTimer: number | null = null;
let currentPath = "";

/** Guard: dieses Environment ist ein Bot / Automation / lokale Entwicklung
 *  und soll GAR NICHTS tracken. Wird bei jedem Track-Call geprüft. */
function isTrackingSuppressed(): boolean {
    if (typeof window === "undefined" || typeof navigator === "undefined") return true;
    // Playwright/Puppeteer/Selenium melden sich freiwillig.
    if ((navigator as { webdriver?: boolean }).webdriver === true) return true;
    // Lokale Entwicklung — kein Public-Traffic.
    const host = window.location.hostname.toLowerCase();
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return true;
    return false;
}

type EventType =
    | "page_view"
    | "heartbeat"
    | "interaction"
    | "conversion"
    | "cta_shown"
    | "cta_clicked"
    | "cta_dismissed"
    | "aktion_banner_shown"
    | "aktion_banner_cta_clicked"
    | "aktion_section_shown"
    | "aktion_section_cta_clicked"
    | "aktion_form_submitted";

interface AttributionPayload {
    fbclid?: string;
    ttclid?: string;
    gclid?: string;
    msclkid?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
}

interface TrackPayload {
    session_id: string;
    event_type: EventType;
    page_path: string;
    referrer?: string;
    device_ua?: string;
    duration_ms?: number;
    vt_code?: string | null;
    custom_data?: Record<string, unknown> | null;
    /** Attribution-Payload (Click-IDs + UTM). Wird beim ersten Event der
     *  Session aus `window.location.search` gelesen und für die
     *  restliche Session in `sessionStorage` gecached. Auf Live erfassen
     *  wir damit auch Meta-Ads-Klicks, die aus dem In-App-Browser kommen
     *  und den Referrer strippen — `fbclid` bleibt dennoch in der URL. */
    attribution?: AttributionPayload | null;
}

/** Attribution-Parameter, die uns interessieren. Die Liste ist bewusst
 *  kompakt: nur die Ad-Plattform-Click-IDs + die 5 klassischen UTM-Felder.
 *  Andere Query-Params (z. B. `?ref=`, `?source=`) ignorieren wir bewusst,
 *  um kein Rauschen einzuschleusen. */
const ATTRIBUTION_KEYS: (keyof AttributionPayload)[] = [
    "fbclid", "ttclid", "gclid", "msclkid",
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
];

/** Liest Click-IDs + UTM-Params aus der aktuellen URL und persistiert sie
 *  in `sessionStorage`. Bereits gespeicherte Attribution wird beibehalten
 *  (erste Berührung gewinnt — First-Touch-Attribution). Rückgabe ist die
 *  aktuell gültige Attribution für die Session (kann `null` sein). */
function getOrCaptureAttribution(): AttributionPayload | null {
    if (typeof window === "undefined") return null;
    try {
        // Vorhandenen Cache lesen — First-Touch-Attribution gewinnt.
        const cached = sessionStorage.getItem(ATTRIBUTION_KEY);
        if (cached) {
            try {
                const parsed = JSON.parse(cached) as AttributionPayload;
                if (parsed && typeof parsed === "object") return parsed;
            } catch { /* fällt durch, wir überschreiben unten */ }
        }
        // Erster Aufruf: aus aktueller URL extrahieren.
        const params = new URLSearchParams(window.location.search);
        const attr: AttributionPayload = {};
        for (const k of ATTRIBUTION_KEYS) {
            const v = params.get(k);
            if (v) {
                // Längenschutz — Click-IDs sind normal < 300 Zeichen.
                attr[k] = v.slice(0, 512);
            }
        }
        if (Object.keys(attr).length === 0) return null;
        sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attr));
        return attr;
    } catch {
        return null;
    }
}

/** Erzeugt eine neue Session-ID (UUID). Fallback für ältere Browser. */
function generateSessionId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function getSessionId(): string {
    try {
        let sid = sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            sid = generateSessionId();
            sessionStorage.setItem(SESSION_KEY, sid);
        }
        return sid;
    } catch {
        // Storage disabled (Safari private mode) → per-page-load ID.
        return generateSessionId();
    }
}

function getVtCode(): string | null {
    try {
        return sessionStorage.getItem(VT_KEY);
    } catch {
        return null;
    }
}

/** Liest `?vt=` aus der URL, speichert in sessionStorage, entfernt aus URL. */
function captureVtFromUrl(): void {
    try {
        const params = new URLSearchParams(window.location.search);
        const vt = (params.get("vt") || "").trim().toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 64);
        if (vt) {
            sessionStorage.setItem(VT_KEY, vt);
            params.delete("vt");
            const clean = params.toString();
            const newUrl =
                window.location.pathname +
                (clean ? `?${clean}` : "") +
                window.location.hash;
            window.history.replaceState({}, "", newUrl);
        }
    } catch {
        /* noop */
    }
}

async function send(payload: TrackPayload): Promise<void> {
    if (isTrackingSuppressed()) return;
    // Defense-in-Depth: Events mit `media_id` beginnend mit `src-`
    // (Fallback-Hash aus alter Frontend-Version für Assets ohne echte
    // DB-UUID) werden komplett verworfen — kein Rauschen in Analytics.
    if (hasFallbackMediaId(payload)) return;
    // Attribution zentral anhängen (First-Touch, aus sessionStorage). Wenn
    // der Caller bereits `attribution` gesetzt hat, respektieren wir das.
    if (payload.attribution === undefined) {
        payload.attribution = getOrCaptureAttribution();
    }
    // Fire-and-forget: kein await auf Response, Frontend darf nie blockieren.
    try {
        await fetch(`${BACKEND_URL}/api/track`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: "omit",
        });
    } catch {
        /* silent — Analytics darf nie den User-Flow stören */
    }
}

/** Prüft ob ein Event eine `src-*`-Fallback-Media-ID enthält. Wird als
 *  finale Defense-in-Depth in `send()` und `sendBeacon()` verwendet —
 *  garantiert, dass unabhängig vom Aufrufer keine Fallback-Hashes ins
 *  Backend gehen. */
function hasFallbackMediaId(payload: TrackPayload): boolean {
    const cd = payload.custom_data;
    if (!cd || typeof cd !== "object") return false;
    const mid = (cd as Record<string, unknown>).media_id;
    return typeof mid === "string" && mid.startsWith("src-");
}

function sendBeacon(payload: TrackPayload): void {
    if (isTrackingSuppressed()) return;
    if (hasFallbackMediaId(payload)) return;
    if (payload.attribution === undefined) {
        payload.attribution = getOrCaptureAttribution();
    }
    if (typeof navigator === "undefined" || !navigator.sendBeacon) {
        void send(payload);
        return;
    }
    try {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        navigator.sendBeacon(`${BACKEND_URL}/api/track`, blob);
    } catch {
        void send(payload);
    }
}

/** Sendet einen `page_view`. Wird aus `useAnalytics()` bei Route-Change gerufen. */
export function trackPageView(pagePath: string): void {
    const now = Date.now();
    const duration_ms = lastEventAt > 0 ? Math.min(now - lastEventAt, 6 * 3600 * 1000) : 0;
    currentPath = pagePath;
    lastEventAt = now;
    void send({
        session_id: getSessionId(),
        event_type: "page_view",
        page_path: pagePath,
        referrer: document.referrer || "",
        device_ua: navigator.userAgent || "",
        duration_ms,
        vt_code: getVtCode(),
    });
}

function trackHeartbeat(): void {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const now = Date.now();
    const duration_ms = lastEventAt > 0 ? Math.min(now - lastEventAt, 6 * 3600 * 1000) : 0;
    if (duration_ms < 1000) return;
    lastEventAt = now;
    void send({
        session_id: getSessionId(),
        event_type: "heartbeat",
        page_path: currentPath || window.location.pathname,
        duration_ms,
        vt_code: getVtCode(),
    });
}

/** Sendet ein Event zuverlässig auch bei sofortiger Navigation/Unmount.
 *  Priorität:
 *    1) `navigator.sendBeacon` — Browser garantiert Zustellung selbst bei
 *       `unload` oder Component-Unmount. Löst den bekannten Bug in In-App-
 *       Browsern (Instagram/Facebook auf iOS), wo `fetch({keepalive: true})`
 *       Klick-Events verliert, sobald ein Popup schließt und ein neues
 *       Modal aufgeht.
 *    2) Fallback auf `send()` (keepalive) wenn sendBeacon fehlt oder wirft.
 *  Für Attribution: `getOrCaptureAttribution()` wird VOR dem Beacon aufge-
 *  rufen, damit `fbclid`/`ttclid` mitfliegen. */
function sendReliable(payload: TrackPayload): void {
    if (isTrackingSuppressed()) return;
    if (hasFallbackMediaId(payload)) return;
    if (payload.attribution === undefined) {
        payload.attribution = getOrCaptureAttribution();
    }
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        try {
            const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
            const ok = navigator.sendBeacon(`${BACKEND_URL}/api/track`, blob);
            if (ok) return;
        } catch {
            /* Beacon-API abgelehnt → Fallback unten */
        }
    }
    void send(payload);
}

/** Feuert bei speziellen Interaktionen (Video-Play, CTA-Klick etc.). */
export function trackInteraction(action: string, custom?: Record<string, unknown>): void {
    lastEventAt = Date.now();
    sendReliable({
        session_id: getSessionId(),
        event_type: "interaction",
        page_path: currentPath || window.location.pathname,
        vt_code: getVtCode(),
        custom_data: { action, ...(custom || {}) },
    });
}

/** Wird bei erfolgreichem Kontaktformular-Submit aufgerufen. */
export function trackConversion(custom?: Record<string, unknown>): void {
    lastEventAt = Date.now();
    sendReliable({
        session_id: getSessionId(),
        event_type: "conversion",
        page_path: currentPath || window.location.pathname,
        vt_code: getVtCode(),
        custom_data: custom || null,
    });
}

/** Post-Viewer-CTA-Tracking: emittiert `cta_shown` / `cta_clicked` /
 *  `cta_dismissed`. `custom.trigger` differenziert nach dem Viewer-Typ,
 *  der zum Öffnen geführt hat (`photo360` / `video360` / `before_after`). */
export function trackCta(
    kind: "shown" | "clicked" | "dismissed",
    custom?: Record<string, unknown>,
): void {
    lastEventAt = Date.now();
    sendReliable({
        session_id: getSessionId(),
        event_type: `cta_${kind}` as EventType,
        page_path: currentPath || window.location.pathname,
        vt_code: getVtCode(),
        custom_data: custom || null,
    });
}

/** Referenz-Aktion-Tracking: Banner + Sektion + Form-Submit. */
export function trackAktion(
    kind: "banner_shown" | "banner_cta_clicked" | "section_shown" | "section_cta_clicked" | "form_submitted"
        | "gutschein_popup_opened" | "gutschein_popup_dismissed" | "gutschein_form_submitted",
    custom?: Record<string, unknown>,
): void {
    lastEventAt = Date.now();
    sendReliable({
        session_id: getSessionId(),
        event_type: `aktion_${kind}` as EventType,
        page_path: currentPath || window.location.pathname,
        vt_code: getVtCode(),
        custom_data: custom || null,
    });
}

/** Für den Kontaktformular-Submit — mitschicken an `/api/contact`. */
export function currentVtCode(): string | null {
    return getVtCode();
}

/** Einmal beim App-Mount rufen (aus `useAnalytics()`). Startet Heartbeat +
 *  registriert unload-Listener. */
function initListeners(): void {
    if ((window as unknown as { __hfAnalyticsInit?: boolean }).__hfAnalyticsInit) return;
    (window as unknown as { __hfAnalyticsInit?: boolean }).__hfAnalyticsInit = true;

    if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
    heartbeatTimer = window.setInterval(trackHeartbeat, HEARTBEAT_INTERVAL_MS);

    // Finaler Heartbeat vor dem Verlassen der Seite.
    const flushOnLeave = () => {
        const now = Date.now();
        const duration_ms = lastEventAt > 0 ? Math.min(now - lastEventAt, 6 * 3600 * 1000) : 0;
        if (duration_ms < 500) return;
        sendBeacon({
            session_id: getSessionId(),
            event_type: "heartbeat",
            page_path: currentPath || window.location.pathname,
            duration_ms,
            vt_code: getVtCode(),
        });
    };
    window.addEventListener("pagehide", flushOnLeave, { capture: true });
    window.addEventListener("beforeunload", flushOnLeave, { capture: true });
}

/** Initialisiert Session + Vt-Code + Listener beim ersten App-Mount. */
export function initAnalyticsOnce(): void {
    // Bei jedem Aufruf idempotent — falls React StrictMode 2× mountet.
    captureVtFromUrl();
    getSessionId(); // sicherstellen, dass eine ID existiert
    initListeners();
}
